import type { UseQueryOptions } from '@tanstack/react-query';
import { MINUTE_MS } from '@/constants/time';
import { legacyRestFetch } from '@/features/account-settings/legacyRestFetch';
import { workspaceQueryCreator } from '@/query/workspaceQueryCreator';

export type GenerateUrlForEmbeddedConnectionQuery = UseQueryOptions<string>;

export function generateUrlForEmbeddedConnectionQueryCreator(
  connectionId: string | number,
): GenerateUrlForEmbeddedConnectionQuery {
  return {
    queryKey: ['generateUrlForEmbeddedConnection', connectionId],
    queryFn: async ({ client }) => {
      const { workspace } =
        (await client.fetchQuery(workspaceQueryCreator())) ?? {};
      if (!workspace?.id) {
        throw new GenerateUrlForEmbeddedConnectionQueryInputError(
          'workspace.id',
        );
      }
      if (!connectionId) {
        throw new GenerateUrlForEmbeddedConnectionQueryInputError(
          'connectionId',
        );
      }

      // TODO(OTWEB-9004): Migrate to recallClient
      const response = await legacyRestFetch(
        'generate_url_for_embedded_connection',
        {
          method: 'GET',
          queryParams: {
            connection_id: connectionId.toString(),
            workspace_id: workspace.id.toString(),
          },
        },
      );

      if (!response.ok) {
        throw new GenerateUrlForEmbeddedConnectionQueryApiCallError();
      }

      const data = await response.json();

      if (data.status !== 'OK') {
        throw new GenerateUrlForEmbeddedConnectionQueryApiError(
          data.status,
          data.message,
        );
      }

      return data.embedded_url as string;
    },
    staleTime: 15 * MINUTE_MS,
  };
}

class GenerateUrlForEmbeddedConnectionQueryInputError extends Error {
  name = 'GenerateUrlForEmbeddedConnectionQueryInputError';
  constructor(inputName: string) {
    super(`Invalid input for ${inputName}`);
  }
}

class GenerateUrlForEmbeddedConnectionQueryApiCallError extends Error {
  name = 'GenerateUrlForEmbeddedConnectionQueryApiCallError';
  constructor() {
    super('An error occurred while calling the API');
  }
}

class GenerateUrlForEmbeddedConnectionQueryApiError extends Error {
  name = 'GenerateUrlForEmbeddedConnectionQueryApiError';
  constructor(status: string, message: string) {
    super(`API error: ${status} - ${message}`);
  }
}
